import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Truck, Route } from "lucide-react";

function CrudTab({ kind, columns, fields, testidPrefix }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = async () => { const { data } = await api.get(`/distributor/${kind}`); setItems(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const save = async () => {
    try { await api.post(`/distributor/${kind}`, form); toast.success("Saved"); setOpen(false); setForm({}); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button onClick={() => setOpen(true)} data-testid={`${testidPrefix}-add`}><Plus className="w-4 h-4 mr-1" /> Add</Button></div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>{columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`${testidPrefix}-row-${i.id}`}>
                {columns.map((c) => <TableCell key={c.key}>{c.render ? c.render(i) : (i[c.key] ?? "—")}</TableCell>)}
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={columns.length} className="text-center text-muted-foreground py-10">No entries.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add {kind.slice(0,-1)}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {fields.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? parseFloat(e.target.value || "0") : e.target.value })} data-testid={`${testidPrefix}-${f.key}`} />
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid={`${testidPrefix}-save`}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Shipments() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = async () => {
    const [s, o, v, d, r] = await Promise.all([
      api.get("/distributor/shipments"),
      api.get("/dealer/orders").catch(() => ({ data: [] })),  // dealer orders fallback (sales-mgr sees all)
      api.get("/distributor/vehicles"),
      api.get("/distributor/drivers"),
      api.get("/distributor/routes"),
    ]);
    setItems(s.data); setOrders(o.data); setVehicles(v.data); setDrivers(d.data); setRoutes(r.data);
  };
  useEffect(() => { load(); }, []);
  const assign = async () => {
    try { await api.post("/distributor/shipments", form); toast.success("Shipment assigned"); setOpen(false); setForm({}); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const pendingOrders = orders.filter((o) => ["CONFIRMED", "PROCESSING"].includes(o.status));
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button onClick={() => setOpen(true)} data-testid="ship-assign-btn"><Truck className="w-4 h-4 mr-1" /> Assign shipment</Button></div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Vehicle</TableHead><TableHead>Driver</TableHead><TableHead>Route</TableHead><TableHead>ETA</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} data-testid={`ship-row-${s.id}`}>
                <TableCell className="font-mono text-xs">{s.order_number}</TableCell>
                <TableCell>{vehicles.find((v) => v.id === s.vehicle_id)?.plate || "—"}</TableCell>
                <TableCell>{drivers.find((d) => d.id === s.driver_id)?.name || "—"}</TableCell>
                <TableCell>{routes.find((r) => r.id === s.route_id)?.name || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{s.expected_at || "—"}</TableCell>
                <TableCell><Badge>{s.status}</Badge></TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No shipments assigned yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign shipment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Order</Label>
              <Select value={form.order_id || ""} onValueChange={(v) => setForm({ ...form, order_id: v })}>
                <SelectTrigger data-testid="ship-order"><SelectValue placeholder="Confirmed/processing dealer order…" /></SelectTrigger>
                <SelectContent>{pendingOrders.map((o) => <SelectItem key={o.id} value={o.id}>{o.number} · ₹{(o.total||0).toLocaleString()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Vehicle</Label>
              <Select value={form.vehicle_id || ""} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                <SelectTrigger data-testid="ship-vehicle"><SelectValue placeholder="Pick vehicle…" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {v.capacity_tons}T</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Driver</Label>
              <Select value={form.driver_id || ""} onValueChange={(v) => setForm({ ...form, driver_id: v })}>
                <SelectTrigger data-testid="ship-driver"><SelectValue placeholder="Pick driver…" /></SelectTrigger>
                <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} · {d.phone}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Route (optional)</Label>
              <Select value={form.route_id || ""} onValueChange={(v) => setForm({ ...form, route_id: v })}>
                <SelectTrigger><SelectValue placeholder="Pick route…" /></SelectTrigger>
                <SelectContent>{routes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Expected delivery</Label><Input type="datetime-local" value={form.expected_at || ""} onChange={(e) => setForm({ ...form, expected_at: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={assign} disabled={!form.order_id || !form.vehicle_id || !form.driver_id} data-testid="ship-save">Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Logistics() {
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="distributor-logistics">
      <div><h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2"><Route className="w-7 h-7 text-primary" /> Logistics</h1><p className="text-muted-foreground">Vehicles, drivers, routes and shipment assignment.</p></div>
      <Tabs defaultValue="shipments">
        <TabsList>
          <TabsTrigger value="shipments" data-testid="log-tab-shipments">Shipments</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="log-tab-vehicles">Vehicles</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="log-tab-drivers">Drivers</TabsTrigger>
          <TabsTrigger value="routes" data-testid="log-tab-routes">Routes</TabsTrigger>
        </TabsList>
        <TabsContent value="shipments"><Shipments /></TabsContent>
        <TabsContent value="vehicles">
          <CrudTab kind="vehicles" testidPrefix="veh"
            columns={[{key:"plate",label:"Plate"},{key:"capacity_tons",label:"Capacity (T)"},{key:"model",label:"Model"},{key:"active",label:"Active",render:(i)=>i.active?"Yes":"No"}]}
            fields={[{key:"plate",label:"Plate"},{key:"capacity_tons",label:"Capacity (tons)",type:"number"},{key:"model",label:"Model"}]} />
        </TabsContent>
        <TabsContent value="drivers">
          <CrudTab kind="drivers" testidPrefix="drv"
            columns={[{key:"name",label:"Name"},{key:"phone",label:"Phone"},{key:"licence_no",label:"Licence"}]}
            fields={[{key:"name",label:"Name"},{key:"phone",label:"Phone"},{key:"licence_no",label:"Licence #"}]} />
        </TabsContent>
        <TabsContent value="routes">
          <CrudTab kind="routes" testidPrefix="rt"
            columns={[{key:"name",label:"Name"},{key:"origin",label:"Origin"},{key:"distance_km",label:"Distance (km)"},{key:"eta_hours",label:"ETA (h)"}]}
            fields={[{key:"name",label:"Name"},{key:"origin",label:"Origin"},{key:"distance_km",label:"Distance (km)",type:"number"},{key:"eta_hours",label:"ETA (h)",type:"number"}]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
